import { useOrganization } from '@/contexts/OrganizationContext';
import { getCurrencyLocale } from '@/lib/currency';

interface NumberDisplayProps {
  value: number;
  /** Fraction digits shown (the maximum when `minDecimals` is set). */
  decimals?: number;
  /**
   * Fewest fraction digits; defaults to `decimals` (fixed). Set `0` to drop
   * trailing zeros — a whole 3 renders "3", 3.5 renders "3.5".
   */
  minDecimals?: number;
  unit?: string;
  /** Trailing string attached directly to the number with no space (e.g. "%"). */
  suffix?: string;
  className?: string;
}

/**
 * Display a formatted number using the organization's locale settings.
 * Automatically formats numbers according to the organization's currency locale
 * (e.g., 1,234.56 for USD, 1.234,56 for EUR).
 *
 * `unit` renders space-separated ("12 kg CO2e"); `suffix` attaches directly ("12.5%").
 *
 * @example
 * <NumberDisplay value={12345.6789} decimals={3} unit="kg CO2e" />
 * // For EUR org: "12.345,679 kg CO2e"
 * // For USD org: "12,345.679 kg CO2e"
 * <NumberDisplay value={12.5} decimals={2} suffix="%" /> // "12.50%"
 * <NumberDisplay value={3} decimals={2} minDecimals={0} /> // "3"
 */
export function NumberDisplay({
  value,
  decimals = 2,
  minDecimals,
  unit,
  suffix,
  className = ''
}: NumberDisplayProps) {
  const { selectedOrganization } = useOrganization();

  const currency = selectedOrganization?.currency || 'EUR';
  const locale = getCurrencyLocale(currency);

  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: Math.min(minDecimals ?? decimals, decimals),
    maximumFractionDigits: decimals,
  }).format(value);

  return (
    <span className={className}>
      {formattedNumber}{suffix}{unit && ` ${unit}`}
    </span>
  );
}
